import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";
import { Container, Modal } from "reactstrap";

import { Heading } from "components/Heading";
import { CustomButton } from "components/CustomButton";
import { PageHeadings, PageTitles } from "../../constants";
import {
  alignPreviewApi,
  alignSchoolsSearchApi,
  alignUserApi,
  alignUsersSearchApi,
} from "../../api/api.service";
import "./Styles.scss";

/**
 * Places an existing account under a school: the teacher who signed up on their own, picked the
 * wrong school, or typed a name that made a duplicate. Statistics count a reset for the school of
 * its CLASSROOM and a person for their own school, so until both are right the school's numbers
 * stay still however much the teacher does.
 *
 * Three steps (find the person, find the school, review), and nothing changes until Confirm. What
 * moves, what stays and why is shown before anyone presses it; the server works all of it out
 * again from the database when it runs, so this screen is advice, never the decision.
 */

const BLOCKERS = {
  is_administrator: "This person is a school or district administrator, so they can't be moved here.",
  mcap_access: "This account has MCAP access, so it can't be moved here.",
  already_there: "This person is already at that school.",
};

const STAY_REASON = {
  shared: "shared with other teachers",
  clever: "synced from Clever",
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** A search box that lists matches under it and calls onPick with the one chosen. */
const Finder = ({ id, label, placeholder, search, render, onPick }) => {
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  // Debounced; an answer for words the user has since changed is dropped.
  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) {
      setRows([]);
      setBusy(false);
      return undefined;
    }
    let live = true;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const res = await search({ q });
        if (live) setRows(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        if (live) {
          setRows([]);
          toast.error(e?.message || "The search failed.");
        }
      } finally {
        if (live) setBusy(false);
      }
    }, 300);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [text, search]);

  const q = text.trim();
  return (
    <div>
      <label className="form-label font-size-13 mb-2" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="form-control au-input"
        autoComplete="off"
        maxLength={100}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
      />
      {q.length >= 2 && (
        <div className="au-results" role="listbox" aria-label={`${label} results`}>
          {busy && <div className="au-note">Searching…</div>}
          {!busy && rows.length === 0 && <div className="au-note">No match.</div>}
          {!busy &&
            rows.map((row) => (
              <button
                key={row.id}
                type="button"
                role="option"
                aria-selected="false"
                className="au-result"
                onClick={() => onPick(row)}
              >
                {render(row)}
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

/** The chosen person or school, with a way to choose again. */
const Picked = ({ title, onChange, disabled, children }) => (
  <div className="au-picked">
    <div className="au-body">
      <div className="au-eyebrow">{title}</div>
      {children}
    </div>
    <CustomButton title="Change" color="light" className="btn-sm au-btn--small" onClick={onChange} disabled={disabled} />
  </div>
);

const Warning = ({ w }) => {
  if (w.code === "other_district") {
    return (
      <>
        Currently in another district{w.detail ? ` (${w.detail})` : ""}. Placing them here takes them out of it.
      </>
    );
  }
  return <>Holds a seat on another plan{w.detail ? ` (${w.detail})` : ""}. The seat is released, so that school no longer lists or counts them.</>;
};

/** What happens to their place on the new school's plan, said before and after the move. */
const SEAT_PREVIEW = {
  will_grant: { tone: "info", text: "They get a place on the new school's plan, so they show in its Manage Users and seat count." },
  kept: { tone: "info", text: "They already hold a place on a plan that covers the new school, so it stays." },
  no_plan: {
    tone: "warning",
    text: "The new school has no plan to seat them on. They are moved without a seat; invite them once it has one.",
  },
  no_free_seat: {
    tone: "warning",
    text: "The new school's plan has no free place. They are moved without a seat; free one up, then invite them.",
  },
};

const SEAT_DONE = {
  granted: { icon: "mdi-account-check-outline", text: "Given a place on the new school's plan. They show in its Manage Users and seat count." },
  kept: { icon: "mdi-account-check-outline", text: "Keeps their place on a plan that covers the new school." },
  no_plan: { icon: "mdi-information-outline", text: "No seat at the new school: it has no plan yet. Invite them once it does." },
  no_free_seat: { icon: "mdi-information-outline", text: "No seat at the new school: its plan is full. Free a place, then invite them." },
};

/** One numbered step of the page, in a card. */
const Step = ({ n, title, span = false, live = false, children }) => (
  <section className={`au-card${span ? " au-span" : ""}`} aria-live={live ? "polite" : undefined}>
    <div className="au-step">
      <span className="au-num" aria-hidden="true">
        {n}
      </span>
      <h5>
        <span className="visually-hidden">{n}. </span>
        {title}
      </h5>
    </div>
    {children}
  </section>
);

/** A dialog with its own close button, a backdrop that dims and blurs the page, and a soft entrance. */
const Dialog = ({ isOpen, onDismiss, onClosed, labelledBy, children }) => (
  <Modal
    isOpen={isOpen}
    toggle={onDismiss}
    onClosed={onClosed}
    centered
    labelledBy={labelledBy}
    modalClassName="align-modal"
    backdropClassName="align-backdrop"
  >
    <div className="au-dialog">
      <button type="button" className="au-x" aria-label="Close" onClick={onDismiss}>
        <i className="mdi mdi-close" aria-hidden="true" />
      </button>
      {children}
    </div>
  </Modal>
);

const AlignUsers = () => {
  const [person, setPerson] = useState(null);
  const [school, setSchool] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [notify, setNotify] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [doneOpen, setDoneOpen] = useState(false);

  // The review, whenever both are chosen. An answer for a pair the user has since changed is dropped.
  useEffect(() => {
    setPlan(null);
    if (!person || !school) return undefined;
    let live = true;
    setLoadingPlan(true);
    alignPreviewApi({ userId: person.id, schoolId: school.id })
      .then((res) => {
        if (live) setPlan(res);
      })
      .catch((e) => {
        if (live) toast.error(e?.message || "Could not work out what would move.");
      })
      .finally(() => {
        if (live) setLoadingPlan(false);
      });
    return () => {
      live = false;
    };
  }, [person, school]);

  const blocked = !!plan && plan.blockers.length > 0;
  const moving = plan?.classrooms.moving ?? [];
  const staying = plan?.classrooms.staying ?? [];

  const startOver = () => {
    setPerson(null);
    setSchool(null);
    setPlan(null);
    setDone(null);
    setNotify(true);
  };

  const confirm = async () => {
    if (!plan || blocked || busy) return;
    setBusy(true);
    try {
      const res = await alignUserApi({ userId: person.id, schoolId: school.id, notify });
      toast.success(`${plan.user.name} is now part of ${plan.target.name}.`);
      setConfirming(false);
      // The names are kept with the result: the dialog stays up while it fades, after the page is reset.
      setDone({ ...res, personName: plan.user.name, schoolName: plan.target.name });
      setDoneOpen(true);
    } catch (e) {
      setConfirming(false);
      toast.error(e?.message || "Could not place them under that school.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-content align-users">
      <Helmet>
        <title>{PageTitles.ALIGN_USERS}</title>
      </Helmet>
      <Container fluid>
        <Heading heading={PageHeadings.ALIGN_USERS} />
        <p className="au-lede">
          Place an existing account under a school: someone who signed up on their own, picked the wrong school, or
          created a duplicate. Their own classrooms move with them, so the school&apos;s statistics start counting what
          they do. You review exactly what moves before anything changes.
        </p>

        <div className="au-grid">
          <Step n={1} title="The person">
            {person ? (
              <Picked title="Person" onChange={() => setPerson(null)} disabled={busy}>
                <strong>{person.name}</strong>
                <div className="font-size-13">{person.email}</div>
                <div className="au-sub">
                  {person.school
                    ? `Now at ${person.school.name}${person.school.districtName ? `, ${person.school.districtName}` : ""}`
                    : "No school yet"}
                </div>
              </Picked>
            ) : (
              <Finder
                id="align-person"
                label="Email or name"
                placeholder="teacher@example.org"
                search={alignUsersSearchApi}
                onPick={setPerson}
                render={(u) => (
                  <div>
                    <strong>{u.name}</strong> <span className="text-muted">{u.email}</span>
                    <div className="au-sub">
                      {u.school ? u.school.name : "No school"}
                      {u.isAdministrator ? " · administrator" : ""}
                    </div>
                  </div>
                )}
              />
            )}
          </Step>

          <Step n={2} title="The school">
            {school ? (
              <Picked title="School" onChange={() => setSchool(null)} disabled={busy}>
                <strong>{school.name}</strong>
                <div className="au-sub">{[school.districtName, school.state].filter(Boolean).join(" · ") || "No district"}</div>
              </Picked>
            ) : (
              <Finder
                id="align-school"
                label="School name"
                placeholder="Scenic Elementary"
                search={alignSchoolsSearchApi}
                onPick={setSchool}
                render={(s) => (
                  <div>
                    <strong>{s.name}</strong>
                    <div className="au-sub">{[s.districtName, s.state].filter(Boolean).join(" · ") || "No district"}</div>
                  </div>
                )}
              />
            )}
          </Step>

          {person && school && (
            <Step n={3} title="Review" span live>
              {loadingPlan && (
                <div className="au-working">
                  <i className="mdi mdi-loading au-spin" aria-hidden="true" />
                  Working out what would move…
                </div>
              )}
              {plan && (
                <>
                  {plan.blockers.map((b) => (
                    <div key={b} className="au-alert au-alert--danger">
                      <i className="mdi mdi-alert-circle-outline" aria-hidden="true" />
                      <span>{BLOCKERS[b] || "This move can't go ahead."}</span>
                    </div>
                  ))}
                  {plan.warnings.map((w) => (
                    <div key={w.code} className="au-alert au-alert--warning">
                      <i className="mdi mdi-alert-outline" aria-hidden="true" />
                      <span>
                        <Warning w={w} />
                      </span>
                    </div>
                  ))}

                  {plan.blockers.length === 0 && SEAT_PREVIEW[plan.seat?.outcome] && (
                    <div className={`au-alert au-alert--${SEAT_PREVIEW[plan.seat.outcome].tone}`}>
                      <i
                        className={`mdi ${SEAT_PREVIEW[plan.seat.outcome].tone === "info" ? "mdi-account-check-outline" : "mdi-alert-outline"}`}
                        aria-hidden="true"
                      />
                      <span>{SEAT_PREVIEW[plan.seat.outcome].text}</span>
                    </div>
                  )}

                  <div className="au-route">
                    <strong>{plan.user.name}</strong>
                    <i className="mdi mdi-arrow-right" aria-hidden="true" />
                    <strong>{plan.target.name}</strong>
                  </div>

                  <div className="au-lists">
                    <div className="au-list">
                      <div className="au-eyebrow mb-1">Moves</div>
                      {moving.length === 0 && <div className="font-size-13 py-2 text-muted">No classrooms to move.</div>}
                      {moving.map((c) => (
                        <div key={c.id} className="au-row">
                          <span>{c.name}</span>
                          <span>{plural(c.resets, "reset", "resets")} go with it</span>
                        </div>
                      ))}
                    </div>

                    {staying.length > 0 && (
                      <div className="au-list au-list--stays">
                        <div className="au-eyebrow mb-1">Stays where it is</div>
                        {staying.map((c) => (
                          <div key={c.id} className="au-row">
                            <span>{c.name}</span>
                            <span>{STAY_REASON[c.reason]}</span>
                          </div>
                        ))}
                        <div className="au-sub mt-2">
                          If their current classroom is one of these, a copy is made at the new school for them to
                          carry on in.
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="au-footer">
                    <div className="form-check mb-0">
                      <input
                        id="align-notify"
                        className="form-check-input"
                        type="checkbox"
                        checked={notify}
                        onChange={(e) => setNotify(e.target.checked)}
                      />
                      <label className="form-check-label font-size-13" htmlFor="align-notify">
                        Email {plan.user.name} that they were placed under this school
                      </label>
                    </div>
                    <CustomButton
                      title="Confirm"
                      color="primary"
                      className="au-btn"
                      disabled={blocked || busy}
                      onClick={() => setConfirming(true)}
                    />
                  </div>
                </>
              )}
            </Step>
          )}
        </div>

        <Dialog
          isOpen={confirming}
          onDismiss={() => !busy && setConfirming(false)}
          labelledBy="align-confirm-title"
        >
          <span className="au-badge" aria-hidden="true">
            <i className="mdi mdi-account-switch-outline" />
          </span>
          <h5 id="align-confirm-title" className="au-title">
            Place {plan?.user.name} under {plan?.target.name}?
          </h5>
          <p className="au-text">
            {plural(moving.length, "classroom", "classrooms")} and {plural(plan?.resetsMoving ?? 0, "reset", "resets")}{" "}
            will move to this school, and the school&apos;s statistics will count them. This is recorded in the audit
            log with your name.
          </p>
          <div className="au-actions">
            <CustomButton
              title="Cancel"
              color="light"
              className="au-modal-btn"
              onClick={() => setConfirming(false)}
              disabled={busy}
            />
            <CustomButton
              title="Place under school"
              color="primary"
              className="au-modal-btn"
              loading={busy}
              onClick={confirm}
            />
          </div>
        </Dialog>

        <Dialog
          isOpen={doneOpen}
          onDismiss={() => setDoneOpen(false)}
          onClosed={startOver}
          labelledBy="align-done-title"
        >
          {done && (
            <div role="status">
              <span className="au-badge" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path className="au-tick" d="M5 12.5l4.5 4.5L19 7.5" />
                </svg>
              </span>
              <h5 id="align-done-title" className="au-title">
                Done.
              </h5>
              <p className="au-text">
                {done.personName} is now part of {done.schoolName}.
              </p>
              <ul className="au-facts">
                <li>
                  <i className="mdi mdi-check" aria-hidden="true" />
                  <span>
                    {plural(done.movedClassrooms, "classroom", "classrooms")} moved with{" "}
                    {plural(done.resetsMoved, "reset", "resets")}.
                  </span>
                </li>
                {done.stayedClassrooms > 0 && (
                  <li>
                    <i className="mdi mdi-information-outline" aria-hidden="true" />
                    <span>
                      {plural(done.stayedClassrooms, "classroom", "classrooms")} stayed where they were (shared or from
                      Clever).
                    </span>
                  </li>
                )}
                {done.newClassroom && (
                  <li>
                    <i className="mdi mdi-folder-swap-outline" aria-hidden="true" />
                    <span>A classroom was created at the new school for them to carry on in.</span>
                  </li>
                )}
                {done.seatsReleased > 0 && (
                  <li>
                    <i className="mdi mdi-account-minus-outline" aria-hidden="true" />
                    <span>Removed from the old school&apos;s Manage Users and seat count.</span>
                  </li>
                )}
                {SEAT_DONE[done.seatOutcome] && (
                  <li>
                    <i className={`mdi ${SEAT_DONE[done.seatOutcome].icon}`} aria-hidden="true" />
                    <span>{SEAT_DONE[done.seatOutcome].text}</span>
                  </li>
                )}
                <li>
                  <i className={`mdi ${done.emailed ? "mdi-email-check-outline" : "mdi-email-off-outline"}`} aria-hidden="true" />
                  <span>{done.emailed ? "They were emailed." : "No email was sent."}</span>
                </li>
              </ul>
              <p className="au-fine">
                School statistics are cached for about two minutes, so the school&apos;s numbers update shortly.
              </p>
              <div className="au-actions">
                <CustomButton
                  title="Align someone else"
                  color="primary"
                  className="au-modal-btn"
                  onClick={() => setDoneOpen(false)}
                />
              </div>
            </div>
          )}
        </Dialog>
      </Container>
    </div>
  );
};

export default AlignUsers;
